<template>
    <div class="creative-form" v-loading.fullscreen.lock="fullscreenLoading">
        <div class="form-container">
            <div class="header-actions">
                <h2 class="title">Создание {{ campaignType === 'rtb' ? 'RTB' : 'Guaranteed' }}-креатива</h2>
            </div>

            <div class="layout">
                <form @submit.prevent="submitForm" class="form">
                    <label class="field">
                        <span>Бренд</span>
                        <select v-model="form.brandId">
                            <option value="">Выберите бренд</option>
                            <option v-for="brand in brands" :key="brand.id" :value="brand.id">
                                {{ brand.name }}
                            </option>
                        </select>
                    </label>

                    <label class="field required">
                        <span>
                            Name
                            <img v-if="!form.name" alt="red_star" class="is-empty" src="/images/red_star.svg" />
                        </span>
                        <input v-model="form.name" type="text" required />
                    </label>

                    <label class="field">
                        <span>Description</span>
                        <textarea v-model="form.description"></textarea>
                    </label>

                    <label class="field">
                        <span>Comment</span>
                        <textarea v-model="form.comment"></textarea>
                    </label>

                    <button type="submit" class="btn btn--primary" :disabled="!isFormValid">
                        Сохранить
                    </button>
                </form>

                <div class="uploads-column">
                    <div
                        v-for="slot in uploadSlots"
                        :key="slot.locale"
                        class="file-upload-section"
                    >
                        <h3 class="subtitle">{{ slot.label }}</h3>
                        <label class="file-upload-area">
                            <input
                                type="file"
                                accept="image/*,video/*"
                                @change="onFileChange($event, slot.locale)"
                                required
                            />
                            <div class="file-upload-content">
                                <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                    <polyline points="17 8 12 3 7 8"/>
                                    <line x1="12" y1="3" x2="12" y2="15"/>
                                </svg>
                                <template v-if="files[slot.locale]">
                                    <span class="file-name">{{ files[slot.locale].name }}</span>
                                    <span class="change-file-text">Выбрать другой файл</span>
                                </template>
                                <template v-else>
                                    <span class="upload-text">Выберите файл</span>
                                    <span class="file-not-selected">Файл не выбран</span>
                                </template>
                            </div>
                        </label>

                        <div class="preview" v-if="previewUrls[slot.locale] && mediaMeta[slot.locale].kind">
                            <img
                                v-if="mediaMeta[slot.locale].kind === 'picture'"
                                :src="previewUrls[slot.locale]"
                                alt="preview"
                            />
                            <video
                                v-if="mediaMeta[slot.locale].kind === 'video'"
                                :src="previewUrls[slot.locale]"
                                controls
                            ></video>
                        </div>

                        <div v-if="hasSlotDimensions(slot.locale)" class="file-upload-specs">
                            <label class="field">
                                <span>Width</span>
                                <input
                                    :value="mediaMeta[slot.locale].width"
                                    readonly
                                    class="field-input--readonly"
                                    type="number"
                                />
                            </label>
                            <label class="field">
                                <span>Height</span>
                                <input
                                    :value="mediaMeta[slot.locale].height"
                                    readonly
                                    class="field-input--readonly"
                                    type="number"
                                />
                            </label>
                            <label v-if="mediaMeta[slot.locale].kind === 'video'" class="field">
                                <span>Duration (мс)</span>
                                <input
                                    :value="mediaMeta[slot.locale].duration"
                                    readonly
                                    class="field-input--readonly"
                                    type="number"
                                />
                            </label>
                        </div>
                        <p
                            v-if="hasSlotDimensions(slot.locale) && durationNormalizeHint(slot.locale)"
                            class="file-upload-duration-hint"
                        >
                            {{ durationNormalizeHint(slot.locale) }}
                        </p>
                        <p v-else-if="!hasSlotDimensions(slot.locale)" class="file-upload-specs-hint">
                            Прикрепите файл — параметры заполнятся автоматически
                        </p>
                    </div>

                    <p v-if="uploadMismatchMessage" class="upload-mismatch">{{ uploadMismatchMessage }}</p>

                    <label class="with-sound-checkbox" v-if="resolvedType === 'video'">
                        <input v-model="form.hasAudioTrack" type="checkbox" />
                        <span class="with-sound-label">With Sound</span>
                    </label>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import {
    DURATION_MAX_MS,
    DURATION_MIN_MS,
    PICTURE_DURATION_MS,
    dimensionsMatch,
    durationsMatch,
    durationsRoundToSameSecond,
    emptyMediaMeta,
    formatDurationNormalizeHint,
    hasDimensions,
    nearestDurationMs,
    readMediaMeta,
} from '../../../utils/mediaMeta';

const UPLOAD_SLOTS = [
    { locale: 'ru', label: 'Русская версия (RU)' },
    { locale: 'kz', label: 'Казахская версия (KZ)' },
];

export default {
    name: 'CreativeCreate',
    props: {
        campaignType: {
            type: String,
            required: true,
            validator: (value) => ['rtb', 'guaranteed'].includes(value),
        },
        brands: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            uploadSlots: UPLOAD_SLOTS,
            files: { ru: null, kz: null },
            previewUrls: { ru: null, kz: null },
            mediaMeta: {
                ru: emptyMediaMeta(),
                kz: emptyMediaMeta(),
            },
            fullscreenLoading: false,
            formSubmitted: false,
            form: {
                name: '',
                brandId: '',
                description: '',
                comment: '',
                hasAudioTrack: false,
            },
        };
    },
    computed: {
        submitUrl() {
            return `/${this.campaignType}/creatives/create`;
        },
        redirectUrl() {
            return `/${this.campaignType}/creatives`;
        },
        resolvedType() {
            const ruKind = this.mediaMeta.ru.kind;
            const kzKind = this.mediaMeta.kz.kind;
            if (ruKind && kzKind && ruKind !== kzKind) return '';
            return ruKind || kzKind || '';
        },
        uploadMismatchMessage() {
            const ru = this.mediaMeta.ru;
            const kz = this.mediaMeta.kz;
            if (!hasDimensions(ru) || !hasDimensions(kz)) return '';
            if (ru.kind !== kz.kind) {
                return 'RU и KZ файлы должны быть одного типа (видео или изображение)';
            }
            if (!dimensionsMatch(ru, kz)) {
                return 'Размер RU и KZ файлов должен совпадать';
            }
            if (ru.kind === 'video' && !durationsMatch(ru, kz)) {
                return 'Длительность RU и KZ версий должна совпадать';
            }
            if (ru.kind === 'video' && !durationsRoundToSameSecond(ru, kz)) {
                return 'После приведения к целой секунде длительности RU и KZ не совпадут';
            }
            if (ru.kind === 'video') {
                const targetMs = nearestDurationMs(ru.duration);
                if (targetMs < DURATION_MIN_MS || targetMs > DURATION_MAX_MS) {
                    return 'Длительность видео должна быть от 1 до 60 с';
                }
            }
            return '';
        },
        isFormValid() {
            return (
                this.form.name.trim() !== '' &&
                this.resolvedType !== '' &&
                this.files.ru !== null &&
                this.files.kz !== null &&
                hasDimensions(this.mediaMeta.ru) &&
                hasDimensions(this.mediaMeta.kz) &&
                this.uploadMismatchMessage === ''
            );
        },
    },
    beforeUnmount() {
        this.revokePreview('ru');
        this.revokePreview('kz');
    },
    methods: {
        hasSlotDimensions(locale) {
            return hasDimensions(this.mediaMeta[locale]);
        },
        revokePreview(locale) {
            const url = this.previewUrls[locale];
            if (url) {
                URL.revokeObjectURL(url);
                this.previewUrls[locale] = null;
            }
        },
        clearSlot(locale) {
            this.revokePreview(locale);
            this.files[locale] = null;
            this.mediaMeta[locale] = emptyMediaMeta();
        },
        async onFileChange(event, locale) {
            const file = event.target.files[0];
            if (!file) return;

            this.revokePreview(locale);
            this.files[locale] = file;
            this.previewUrls[locale] = URL.createObjectURL(file);
            this.mediaMeta[locale] = emptyMediaMeta();

            if (locale === 'ru') {
                this.form.name = file.name.replace(/\.[^/.]+$/, '');
            }

            if (file.type.startsWith('video/')) {
                this.form.hasAudioTrack = true;
            }

            try {
                const meta = await readMediaMeta(file);
                if (this.files[locale] !== file) return;
                this.mediaMeta[locale] = meta;
            } catch (error) {
                if (this.files[locale] !== file) return;
                this.clearSlot(locale);
                event.target.value = '';
                this.$notify({
                    title: 'Ошибка',
                    message: error.message || 'Не удалось прочитать файл',
                    type: 'error',
                });
            }
        },
        slotDuration(locale) {
            const meta = this.mediaMeta[locale];
            if (meta.kind === 'video') return meta.duration;
            return PICTURE_DURATION_MS;
        },
        durationNormalizeHint(locale) {
            const meta = this.mediaMeta[locale];
            if (meta.kind !== 'video' || !meta.duration) return '';
            return formatDurationNormalizeHint(meta.duration);
        },
        async submitForm() {
            if (!this.isFormValid) {
                this.formSubmitted = true;
                return;
            }

            this.formSubmitted = false;
            this.fullscreenLoading = true;

            const formData = new FormData();
            formData.append('file_ru', this.files.ru);
            formData.append('file_kz', this.files.kz);
            formData.append('campaign_type', this.campaignType);
            formData.append('type', this.resolvedType);
            formData.append('name', this.form.name);

            if (this.form.brandId) {
                const selectedBrand = this.brands.find((b) => b.id === this.form.brandId);
                formData.append('brand_id', selectedBrand ? selectedBrand.id : this.form.brandId);
                formData.append('brand', selectedBrand ? selectedBrand.name : '');
            } else {
                formData.append('brand_id', '');
                formData.append('brand', '');
            }

            formData.append('description', this.form.description || '');
            formData.append('comment', this.form.comment || '');
            formData.append('hasAudioTrack', this.form.hasAudioTrack ? '1' : '0');
            formData.append('duration', String(this.slotDuration('ru')));
            formData.append('duration_kz', String(this.slotDuration('kz')));
            formData.append('width', String(this.mediaMeta.ru.width));
            formData.append('height', String(this.mediaMeta.ru.height));

            try {
                const response = await axios.post(this.submitUrl, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.data.success) {
                    this.$notify({
                        title: 'Успешно',
                        message: response.data.message || 'Креатив создан и ожидает модерации',
                        type: 'success',
                    });
                    window.location.href = this.redirectUrl;
                }
            } catch (error) {
                this.$notify({
                    title: 'Ошибка',
                    message: error.response?.data?.error || 'Не удалось создать креатив',
                    type: 'error',
                });
            } finally {
                this.fullscreenLoading = false;
            }
        },
    },
};
</script>

<style scoped>
.creative-form {
    padding: 2rem;
    max-width: 1400px;
    margin: 0 auto;
}

.form-container {
    background: #fff;
    border-radius: 8px;
    padding: 2rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.header-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
}

.title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
}

.layout {
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    gap: 2rem;
    align-items: start;
}

.uploads-column {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.field.required .is-empty {
    display: inline;
}

.field span {
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
}

.field input,
.field select,
.field textarea {
    padding: 0.5rem 0.65rem;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 0.875rem;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
    outline: none;
    border-color: #007bff;
}

.field input.field-input--readonly {
    background: #f3f4f6;
    color: #374151;
    cursor: default;
}

.field input.field-input--readonly:focus {
    border-color: #d1d5db;
}

.field textarea {
    min-height: 60px;
    resize: vertical;
}

.subtitle {
    font-size: 1rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 0.75rem 0;
}

.file-upload-section {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.25rem;
}

.file-upload-area {
    display: block;
    cursor: pointer;
}

.file-upload-area input[type="file"] {
    display: none;
}

.file-upload-content {
    border: 2px dashed #cbd5e1;
    border-radius: 10px;
    padding: 1.5rem;
    text-align: center;
    transition: all 0.2s;
    background: #fff;
}

.file-upload-area:hover .file-upload-content {
    border-color: #94a3b8;
    background: #f1f5f9;
}

.upload-icon {
    width: 36px;
    height: 36px;
    color: #64748b;
    margin-bottom: 0.5rem;
}

.upload-text {
    display: block;
    font-weight: 500;
    color: #475569;
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
}

.file-name {
    display: block;
    color: #0f172a;
    font-size: 0.8rem;
    font-weight: 500;
    word-break: break-all;
    margin-bottom: 0.35rem;
}

.change-file-text {
    display: block;
    color: #3b82f6;
    font-size: 0.75rem;
    font-weight: 500;
    text-decoration: underline;
}

.file-not-selected {
    color: #94a3b8;
    font-size: 0.75rem;
}

.preview {
    margin-top: 1rem;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
}

.preview img,
.preview video {
    max-width: 100%;
    height: auto;
    display: block;
}

.file-upload-specs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
    gap: 0.75rem;
    margin-top: 1rem;
}

.file-upload-specs-hint {
    margin: 0.85rem 0 0;
    font-size: 0.8rem;
    color: #94a3b8;
}

.file-upload-duration-hint {
    margin: 0.5rem 0 0;
    font-size: 0.8rem;
    color: #475569;
}

.upload-mismatch {
    margin: 0;
    padding: 0.65rem 0.85rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: #991b1b;
    font-size: 0.8rem;
    line-height: 1.4;
}

.with-sound-checkbox {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.75rem;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    cursor: pointer;
}

.with-sound-checkbox input {
    width: 14px;
    height: 14px;
    accent-color: #3b82f6;
}

.with-sound-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #1e40af;
}

.is-empty {
    width: 10px;
    height: 10px;
    margin-left: 3px;
}

@media (max-width: 768px) {
    .layout {
        grid-template-columns: 1fr;
    }
}
</style>
